import {FilterList, Property} from './widgets';

export const enum NotificationType {
  Email = `email`,
  Inapp = `inapp`,
  JourneyProxy = `journey_proxy`,
  Push = `push`,
  SMS = `sms`,
  WebInapp = `web_inapp`,
  Webhook = `webhook`,
}

export const enum MessageStatus {
  Active = `active`,
  Completed = `completed`,
  Draft = `draft`,
  NewMessage = `new-message`,
  Paused = `paused`,
}

export interface BaseMessage {
  id?: number;
  name: string;
  images?: any[];
  weight?: number;
}

export interface EmailMessage extends BaseMessage {
  from_name: string;
  from_addr: string;
  bcc_addr?: string;
  subject: string;
  body: string;
  body_plaintext: string;
  plaintext: boolean;
  show_unsubscribe: boolean;
  is_liquid: boolean;
  track_open: boolean;
  track_links: boolean;
  images?: any[];
  use_stripo: boolean;
  stripo_html: string;
  stripo_css: string;
}

export interface SMSMessage extends BaseMessage {
  body: string;
}

export type SMSMessageFields = {
  [key in keyof SMSMessage]: JQuery<HTMLElement>;
};

export enum PushDeviceType {
  ALL,
  IOS,
  ANDROID,
}

type BasePushMessage = BaseMessage & {
  device_type: PushDeviceType;
  track_open: boolean;
  is_liquid: boolean;
};

export type PushMessageVersion1 = BasePushMessage & {
  body: string;
  custom_data: string;
  version: 1;
};

export type PushMessageVersion2 = BasePushMessage & {
  base_fields: PushMessageBaseFields;
  android_fields: PushMessageAndroidFields;
  android_custom_data: string;
  ios_fields: PushMessageIOSFields;
  ios_custom_data: string;
  version: 2;
};

export type PushMessage = PushMessageVersion1 | PushMessageVersion2;

export enum PushTapActionType {
  HOMESCREEN = `homescreen`,
  URL_IN_BROWSER = `browser`,
  DEEP_LINK = `deeplink`,
}

export enum PushAndroidVisibility {
  PRIVATE = `VISIBILITY_PRIVATE`,
  PUBLIC = `VISIBILITY_PUBLIC`,
  SECRET = `VISIBILITY_SECRET`,
}

export enum FCMPriority {
  NORMAL = `normal`,
  HIGH = `high`,
}

export type PushTapAction = {
  type: PushTapActionType;
  uri?: string;
};

export type PushMessageActionButton = {
  lbl: string;
  id: string;
  ontap: PushTapAction;
};

export type PushMessageKeyValuePair = {key: string; value: string};

export type PushMessageBaseFields = {
  title?: string;
  body?: string;
  image_url?: string;
  ontap?: PushTapAction;
  buttons?: PushMessageActionButton[];
  is_silent_notification?: boolean;
};

export type PushMessageAndroidOverlapBaseFields = {
  mp_title?: string;
  mp_message?: string;
  mp_img?: string;
  mp_ontap?: PushTapAction;
  mp_buttons?: PushMessageActionButton[];
};

export type PushMessageIOSOverlapBaseFields = {
  title?: string;
  body?: string;
  image_url?: string;
  buttons?: PushMessageActionButton[];
  ontap?: PushTapAction;
};

export type PushMessageAndroidFields = {
  mp_bdgcnt?: number;
  mp_buttons?: PushMessageActionButton[];
  mp_channel_id?: string;
  mp_color?: string;
  mp_ontap?: PushTapAction;
  mp_icnm_l?: string;
  mp_icnm?: string;
  mp_img?: string;
  mp_message?: string;
  mp_silent?: boolean;
  mp_sticky?: boolean;
  mp_subtxt?: string;
  mp_tag?: string;
  mp_title?: string;
  mp_time?: string;
  mp_visibility?: PushAndroidVisibility;
  fcm_priority?: string;
  fcm_ttl?: string;
  key_value_pairs?: PushMessageKeyValuePair[];
  custom_data?: string;
};

export type PushMessageIOSFields = {
  title?: string;
  body?: string;
  badge?: string;
  media_url?: string;
  sound?: string;
  category?: string;
  buttons?: PushMessageActionButton[];
  ontap?: PushTapAction;
  key_value_pairs?: PushMessageKeyValuePair[];
  custom_data?: string;
};

export interface WebhookMessage extends BaseMessage {
  url: string;
}

export type WebhookMessageFields = {
  [key in keyof WebhookMessage]: JQuery<HTMLElement>;
};

export interface InAppMessage extends BaseMessage {
  bg_color: number;
  body: string;
  button_color: number;
  cta_url_android: string;
  cta_url: string;
  cta: string;
  display_triggers?: Record<string, any>;
  dont_send_legacy: boolean;
  hide_text: boolean;
  image_data?: {
    images: any[];
    image_url: string;
    preview_url: string;
  };
  image_fade: boolean;
  image_url?: string;
  inapp_version: number;
  is_liquid: boolean;
  second_button: boolean;
  second_cta_url_android?: string;
  second_cta_url?: string;
  second_cta?: string;
  style: `dark` | `light`;
  text_color: number;
  title: string;
  transparent_bg: boolean;
  type: `takeover` | `mini`;
}

export interface WebInAppImageData {
  file: Record<string, any>;
  storage_filename: string;
  blob_url: string;
  b64_data?: string | ArrayBuffer;
}

export enum WebInAppType {
  TAKEOVER = `takeover`,
  MINI = `mini`,
  CUSTOM_HTML = `custom_html`,
}

export interface WebInAppMessage extends BaseMessage {
  type: WebInAppType;
  title: string;
  body: string;
  cta: string;
  cta_url: string;
  image_url: string;
  thumb_image_url: string;
  video_url: string;
  display_triggers?: Record<string, any>;
  image_data: WebInAppImageData;
  thumb_image_data:
    | {
        mini: WebInAppImageData;
        takeover: WebInAppImageData;
      }
    | WebInAppImageData;
  style: `light` | `dark`;
  is_liquid: boolean;
}

export type Message = EmailMessage | SMSMessage | PushMessage | WebhookMessage | InAppMessage | WebInAppMessage;

export enum Recur {
  ASAP = 8,
  DAILY = 3,
  JOURNEY = 9,
  MONTHLY = 1,
  'ONE-OFF' = 7,
  WEEKLY = 2,
  YEARLY = 0,
}

declare enum Paused {
  FALSE = 0,
  TRUE = 1,
}

export type PeoplePropertySelector = string;
export type EventSelector =
  | string
  | {
      event: string;
    };
export interface BehaviorSelector {
  window: string;
  name: string;
  event_selectors: EventSelector[];
}

export type UserSelector = PeoplePropertySelector | {selector: PeoplePropertySelector; behaviors: BehaviorSelector};

export interface NotificationSchedule {
  max_sends: number;
  paused: Paused;
  rate_limits?: string;
  recur: Recur;
  schedule_timezone?: string;
  start_date?: string;
  stop_date?: string;
  display_triggers?: Array<{event: string}>;
}

export interface SelectorTargeting {
  selector: UserSelector;
  property_filter: TriggerFilterList;
}

export interface SelectedRecipientsTargeting {
  selected_recipients: Array<Record<string, any>>;
}

export interface CohortSelectorTargeting {
  cohort_selector: any;
  cohort_behaviors: any;
}

export type TriggerTargeting = SelectorTargeting | SelectedRecipientsTargeting | CohortSelectorTargeting;

export type TriggerParams = {
  is_draft: boolean;
  trigger_id: number;
  trigger_type: NotificationType;
  messages: Message[];
  schedule: NotificationSchedule;
  device_type: PushDeviceType;
  journey_campaign: boolean;
  recaptcha_response?: any;
} & TriggerTargeting;

export interface TriggerFilterList extends FilterList {
  trigger_cutoff?: {property: Property; selected: string};
}

export type SetTriggerResponse =
  | {
      status: 0;
      errors: {[prop: string]: any} | string[];
    }
  | {
      status: 0;
      error: string;
    }
  | {
      status: 1;
      trigger: Campaign;
      journey_dict?: JourneyDict;
    };

interface TriggerFields {
  behaviors?: any;
  has_unread_update: boolean;
  selected_recipients?: any;
  tz_name: string;
  campaign_id: number;
  paused: boolean;
  schedule_timezone: number;
  version_unique_id: string;
  id: number;
  is_draft: boolean;
  cohort_selector?: any;
  local_stop_date?: any;
  cohort_behaviors?: any;
  user_id: number;
  recur: number;
  needs_verification: boolean;
  project_id: number;
  start_date: string;
  deleted: boolean;
  sampling_ratio: number;
  stop_date?: any;
  is_active: boolean;
  selector: string;
  property_filter: string;
  schedule_timezone_id: number;
  max_sends: number;
  is_editable: boolean;
  version_number: number;
  name: string;
  journey_priority?: any;
  created: string;
  modified: string;
  unpause_when_verified: boolean;
  is_one_off: boolean;
  deprecated_delivery_lock: number;
  delay_time: number;
  local_start_date: string;
  message_type: string;
  in_journey: boolean;
}

export interface Journey {
  delay: number;
  quota_window: number;
  weight: number;
  created: string;
  deleted: boolean;
  modified: string;
  project: number;
  version: number;
  user: number;
  proxy_campaign: number;
  id: number;
}

interface JourneyCampaign {
  fields: TriggerFields;
  trigger_type: string;
  created_by: string;
  pk: number;
}

interface JourneyDict {
  journey: Journey;
  journey_campaigns: JourneyCampaign[];
}

export interface Campaign {
  trigger_type: NotificationType;
  fields: TriggerFields;
  rate_limits: any[];
  messages: Message[];
  created_by: string;
  pk: number;
}
